import { useState } from 'react'
import { useNews } from '../hooks/useNews'

const NewsApp = () => {
  const { 
    news, 
    loading, 
    error, 
    categories, 
    selectedCategory, 
    setSelectedCategory, 
    refresh 
  } = useNews()
  
  const [selectedArticle, setSelectedArticle] = useState(null)
  
  const handleArticleClick = (article) => {
    if (article.url && article.url !== '#') {
      window.open(article.url, '_blank')
    }
  }
  
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'AI/ML': return '🤖'
      case 'Frontend': return '🎨'
      case 'Programming': return '💻'
      case 'Business': return '💼'
      case 'Crypto': return '₿'
      case 'Security': return '🔒'
      default: return '⚡'
    }
  }
  
  const getCategoryColor = (category) => {
    switch (category) {
      case 'AI/ML': return 'bg-blue-500/20 text-blue-300 border-blue-400/30'
      case 'Frontend': return 'bg-pink-500/20 text-pink-300 border-pink-400/30'
      case 'Programming': return 'bg-green-500/20 text-green-300 border-green-400/30'
      case 'Business': return 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30'
      case 'Crypto': return 'bg-orange-500/20 text-orange-300 border-orange-400/30'
      case 'Security': return 'bg-red-500/20 text-red-300 border-red-400/30'
      default: return 'bg-purple-500/20 text-purple-300 border-purple-400/30'
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center gap-3 p-4 sm:p-6 border-b border-white/10">
        <h1 className="text-lg sm:text-2xl font-bold flex items-center min-w-0">
          <i className="fas fa-newspaper mr-2 sm:mr-3 text-orange-400 shrink-0"></i>
          <span className="truncate">Developer News</span>
        </h1>
        <div className="flex items-center space-x-3 shrink-0">
          {/* The attribution is the first thing to go when space is tight; the
              refresh control has to stay reachable. */}
          <span className="hidden lg:flex text-sm text-white/60 items-center">
            <i className="fab fa-hacker-news mr-2"></i>
            Powered by Hacker News
          </span>
          <button
            onClick={refresh}
            disabled={loading}
            aria-label="Refresh news"
            className="px-3 py-2 bg-orange-500/20 hover:bg-orange-500/30 rounded-lg transition-colors text-sm disabled:opacity-50 whitespace-nowrap"
          >
            <i className={`fas fa-sync-alt mr-2 ${loading ? 'fa-spin' : ''}`}></i>
            Refresh
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar with categories. Filtering is an inline action rather than a
            mode, so on narrow screens it becomes a row of chips above the
            articles instead of a drawer you have to open and dismiss. */}
        <div className="hidden md:block w-64 p-6 border-r border-white/10">
          <h3 className="text-lg font-semibold mb-4 text-white/80">Categories</h3>
          <div className="space-y-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`
                  w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between
                  ${selectedCategory === category 
                    ? 'bg-orange-500/20 text-orange-300 border border-orange-400/30' 
                    : 'text-white/70 hover:bg-white/5 hover:text-white/90'
                  }
                `}
              >
                <span className="flex items-center">
                  <span className="mr-2">{getCategoryIcon(category)}</span>
                  {category === 'all' ? 'All Stories' : category}
                </span>
                <span className="text-xs bg-white/10 px-2 py-1 rounded-full">
                  {category === 'all' ? news.length + (news.length !== categories.length - 1 ? '+' : '') : 
                   news.filter(article => article.category === category).length}
                </span>
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="mt-8 p-4 glass-effect rounded-lg">
            <h4 className="text-sm font-semibold text-white/80 mb-3">Today's Stats</h4>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-white/60">Total Articles</span>
                <span className="text-orange-400 font-medium">{news.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Categories</span>
                <span className="text-orange-400 font-medium">{categories.length - 1}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Last Updated</span>
                <span className="text-white/50">Now</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto min-w-0">
          {/* Category chips, the narrow-screen stand-in for the sidebar. The row
              scrolls sideways inside itself so the page never does. */}
          <div className="md:hidden -mx-4 px-4 mb-4 overflow-x-auto">
            <div className="flex gap-2 w-max pb-1">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  aria-pressed={selectedCategory === category}
                  className={`
                    shrink-0 px-3 py-2 rounded-full text-sm whitespace-nowrap transition-colors border
                    ${selectedCategory === category
                      ? 'bg-orange-500/20 text-orange-300 border-orange-400/30'
                      : 'text-white/70 border-white/10 hover:bg-white/5 hover:text-white/90'
                    }
                  `}
                >
                  <span className="mr-1">{getCategoryIcon(category)}</span>
                  {category === 'all' ? 'All' : category}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <i className="fas fa-spinner fa-spin text-4xl text-orange-400 mb-4"></i>
                <p className="text-white/70">Loading latest tech news...</p>
                <p className="text-sm text-white/50 mt-2">Fetching from Hacker News</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle text-4xl text-red-400 mb-4"></i>
                <p className="text-white/70 mb-2">Failed to load news</p>
                <p className="text-sm text-white/50 mb-4">{error}</p>
                <button 
                  onClick={refresh}
                  className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Header for selected category */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">
                  {selectedCategory === 'all' ? 'All Stories' : 
                   `${getCategoryIcon(selectedCategory)} ${selectedCategory}`}
                </h2>
                <p className="text-white/60 text-sm">
                  {news.length} article{news.length !== 1 ? 's' : ''} 
                  {selectedCategory !== 'all' && ` in ${selectedCategory}`}
                </p>
              </div>

              {/* Articles grid */}
              <div className="space-y-4">
                {news.length === 0 ? (
                  <div className="text-center py-16">
                    <i className="fas fa-newspaper text-6xl text-white/20 mb-4"></i>
                    <h3 className="text-xl font-semibold text-white/60 mb-2">No articles found</h3>
                    <p className="text-white/40">
                      {selectedCategory === 'all' 
                        ? 'Try refreshing to load the latest news'
                        : `No articles in the ${selectedCategory} category`
                      }
                    </p>
                  </div>
                ) : (
                  news.map((article, index) => (
                    <div 
                      key={article.id || index}
                      onClick={() => handleArticleClick(article)}
                      className="glass-effect rounded-lg p-4 hover:bg-white/5 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 pr-4">
                          {/* Category badge */}
                          <div className="flex items-center mb-2">
                            <span className={`inline-block px-2 py-1 rounded text-xs border ${getCategoryColor(article.category)}`}>
                              {getCategoryIcon(article.category)} {article.category}
                            </span>
                          </div>

                          {/* Article title */}
                          <h3 className="text-lg font-semibold leading-tight mb-2 text-white/90 group-hover:text-white">
                            {article.title}
                          </h3>

                          {/* Article description */}
                          {article.description && (
                            <p className="text-sm text-white/60 mb-3 leading-relaxed">
                              {article.description}
                            </p>
                          )}

                          {/* Article metadata */}
                          <div className="flex items-center space-x-4 text-xs text-white/50">
                            <span className="flex items-center">
                              <i className="fas fa-user mr-1"></i>
                              {article.author}
                            </span>
                            <span className="flex items-center">
                              <i className="fas fa-clock mr-1"></i>
                              {article.timeAgo}
                            </span>
                            {article.commentCount > 0 && (
                              <span className="flex items-center">
                                <i className="fas fa-comment mr-1"></i>
                                {article.commentCount}
                              </span>
                            )}
                            {article.url && !article.url.includes('news.ycombinator.com') && (
                              <span className="flex items-center">
                                <i className="fas fa-external-link-alt mr-1"></i>
                                {new URL(article.url).hostname.replace('www.', '')}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Score and actions */}
                        <div className="flex-shrink-0 flex flex-col items-center space-y-2">
                          {article.score > 0 && (
                            <div className="text-center">
                              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-orange-500/20 text-orange-400">
                                <div className="text-center">
                                  <i className="fas fa-arrow-up text-sm block"></i>
                                  <span className="text-xs font-medium">{article.score}</span>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <button 
                            onClick={(e) => {
                              e.stopPropagation()
                              handleArticleClick(article)
                            }}
                            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors opacity-0 group-hover:opacity-100"
                            title="Open article"
                          >
                            <i className="fas fa-external-link-alt text-xs"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default NewsApp
